import type { Groups, Relation, RelationCardinality, RelationType, Table } from '@/types'

export type CardinalityKind = RelationCardinality

const RELATION_LABELS: Record<RelationType, string> = {
  '1-N': '1:N',
  '1-1': '1:1',
  '0-1': '0:1',
  '0-N': '0:N',
  'N-N': 'N:N',
}

const RELATION_CARDINALITIES: Record<
  RelationType,
  { from: CardinalityKind; to: CardinalityKind }
> = {
  '1-N': { from: 'many', to: 'one' },
  '1-1': { from: 'one', to: 'one' },
  '0-1': { from: 'one', to: 'zero-one' },
  '0-N': { from: 'many', to: 'zero-one' },
  'N-N': { from: 'many', to: 'many' },
}

function maximumCardinalityLabel(cardinality: CardinalityKind): '1' | 'N' {
  return cardinality === 'many' || cardinality === 'zero-many' ? 'N' : '1'
}

export function getRelationshipLabel(
  relationship: RelationType | Pick<Relation, 'type' | 'fromCardinality' | 'toCardinality'>,
): string {
  if (typeof relationship === 'string') return RELATION_LABELS[relationship]
  const cardinalities = getRelationshipCardinalities(relationship)
  return `${maximumCardinalityLabel(cardinalities.to)}:${maximumCardinalityLabel(cardinalities.from)}`
}

export function getRelationshipCardinalities(
  relationship: RelationType | Pick<Relation, 'type' | 'fromCardinality' | 'toCardinality'>,
): {
  from: CardinalityKind
  to: CardinalityKind
} {
  const type = typeof relationship === 'string' ? relationship : relationship.type
  const legacy = RELATION_CARDINALITIES[type]
  if (typeof relationship === 'string') return legacy
  return {
    from: relationship.fromCardinality ?? legacy.from,
    to: relationship.toCardinality ?? legacy.to,
  }
}

export function relationTypeFromCardinalities(
  from: CardinalityKind,
  to: CardinalityKind,
): RelationType {
  const manySide = from === 'many' || from === 'zero-many'
  const optionalParent = to === 'zero-one'
  if (manySide) return optionalParent ? '0-N' : '1-N'
  return optionalParent ? '0-1' : '1-1'
}

export function getRelationshipColor(
  relationship: Pick<Relation, 'type' | 'from' | 'to'>,
  tables: Pick<Table, 'id' | 'g'>[],
  groups: Groups,
): string {
  if (relationship.type === 'N-N') {
    return '#94A3B8'
  }

  const oneSideTable = tables.find((table) => table.id === relationship.to)
  if (!oneSideTable) {
    return '#94A3B8'
  }

  return groups[oneSideTable.g]?.color ?? '#94A3B8'
}
